/** Transfer 문서 컴포넌트(TransferDocVarField 등)가 패널 공유 상태에 접근하는 provide/inject 컨텍스트.
 *  OTC(otcContext) 미러링의 축소판. 패널이 기존 transfers[]/refs 모델을 이 컨텍스트로 브리지한다. */
import type { InjectionKey, Ref } from 'vue';
import type { TransferFieldType } from './transferFields';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';

export interface TransferDocContext {
  valueOf: (key: string) => string;
  labelOf: (key: string) => string;
  typeOf: (key: string) => TransferFieldType;
  isDerived: (key: string) => boolean;
  activeKey: Ref<string>;
  activate: (key: string) => void;               // 변수 클릭 → 챗봇/입력바/주소 라우팅
  setFieldValue: (key: string, val: string) => void; // 드래그앤드롭·직접 편집

  // ── 조건 토글([[if X]]) ──
  flag: (name: string) => boolean;               // is_multiple_director_company / is_multiple_member / has_common_seal
  toggleFlag: (name: string) => void;
  choiceOf: (name: string) => string;            // resolution_basis: constitution|articles|third_schedule
  setChoice: (name: string, val: string) => void;

  // ── Governing Document (정관 결의 근거) ──
  clauseLocked: Ref<boolean>;                    // entity 등록 clause → 편집 잠금

  // ── 반복부: Transfers ──
  transferCount: Ref<number>;
  addTransfer: () => void;
  removeTransfer: (index: number) => void;       // 1-based
  /** 양도인 증서(Certificate) 콤보 — transferor 선택 시 자동 채움. */
  certOptionsOf: (index: number) => Array<{ title: string; value: string; shares: number }>;
  selectCert: (index: number, certNo: string) => void;

  // ── Section 105 체크박스 그룹 (transfer index 1-based — 양도 1건 = Form 1부) ──
  transfereeKindOf: (index: number) => 'individual' | 'corporate' | '';
  setTransfereeKind: (index: number, k: 'individual' | 'corporate') => void;
  raceOf: (index: number) => string;             // '' | malay | chinese | indian | others
  setRace: (index: number, v: string) => void;
  bodyTypeOf: (index: number) => string;         // '' | gov | mn | mnn | nm
  setBodyType: (index: number, v: string) => void;

  // ── [[Signers]] — 문서별 서명자 선택 ──
  entityCd: Ref<string>;
  signersOf: (varKey: string) => SignerEntry[];
  setSigners: (varKey: string, list: SignerEntry[]) => void;
  /** 서명란 자동 입력 가능 여부(entity 데이터 로드됨). 현재 'waiver' = 기존 주주 − 매도자. */
  canAutofillSigners: (varKey: string) => boolean;
  /** 서명란 자동 입력 실행. */
  autofillSigners: (varKey: string) => void;

  isInvalid: (key: string) => boolean;

  /** 문서 종류가 이 task 에 활성(연결된 템플릿 존재)인지 — 스튜디오가 연결된 문서만 표시(with/without SC 자동 분기). */
  docKindEnabled: (kind: 'waiver' | 'section105' | 'dcr' | 'sharecert') => boolean;
}

export const TRANSFER_DOC_CTX: InjectionKey<TransferDocContext> = Symbol('transferDocCtx');
